/**
 * GestoresPage — bandeja unificada de "Gestores".
 *
 * Tabs:
 *   1) Documentación → solicitudes_doc/my-queue (lo que antes era /solicitudes-doc)
 *   2) Incidencias   → /gestores/incidencias?tipo=incidencia
 *   3) Ampliaciones  → /gestores/incidencias?tipo=ampliacion
 *
 * Visible para colectores + gestores + admin. Cada tab respeta los
 * permisos del backend; si el usuario no es colector, la tab Documentación
 * muestra un mensaje. Si no es gestor, las tabs de Incidencias/Ampliaciones
 * tampoco le mostrarán datos (el backend devuelve 403).
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ClipboardText, Warning, ListPlus, Bell } from "@phosphor-icons/react";
import SolicitudesDocPage from "./SolicitudesDocPage";
import IncidenciasFeed from "@/components/gestores/IncidenciasFeed";
import RecordatoriosFeed from "@/components/gestores/RecordatoriosFeed";

const TABS = [
  { k: "documentacion", label: "Documentación", icon: ClipboardText },
  { k: "incidencias",   label: "Incidencias",   icon: Warning },
  { k: "ampliaciones",  label: "Ampliaciones",  icon: ListPlus },
  { k: "recordatorios", label: "Recordatorios", icon: Bell },
];

export default function GestoresPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const initialTab = params.get("tab");
  const [tab, setTab] = useState(
    TABS.some((t) => t.k === initialTab) ? initialTab : "documentacion",
  );

  useEffect(() => {
    if (params.get("tab") !== tab) {
      const next = new URLSearchParams(params);
      next.set("tab", tab);
      setParams(next, { replace: true });
    }
  }, [tab, params, setParams]);

  const isColector = !!(user?.is_colector || user?.role === "admin");
  const isGestor = ["admin", "gestor_senior", "gestor_siniestros"].includes(user?.role);

  return (
    <div className="bg-background min-h-screen" data-testid="gestores-page">
      <header className="sticky top-0 z-10 bg-white border-b border-border px-4 md:px-6 h-14 flex items-center gap-3">
        <ClipboardText className="w-5 h-5 text-brand" weight="duotone" />
        <h1 className="font-display font-bold text-base md:text-lg">Gestores</h1>
        <p className="hidden md:block text-[11.5px] text-muted ml-2">
          Solicitudes de documentación, incidencias y ampliaciones en un único panel.
        </p>
      </header>

      <div className="px-4 md:px-6 pt-3 pb-4 max-w-6xl mx-auto">
        <div
          className="flex gap-1 bg-white border border-border rounded-lg p-1 inline-flex"
          data-testid="gestores-tabs"
        >
          {TABS.map((t) => {
            const active = tab === t.k;
            const Icon = t.icon;
            return (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`gestores-tab-${t.k}`}
                className={`text-[12.5px] font-semibold px-3 py-1.5 rounded-md inline-flex items-center gap-1.5 transition-colors ${
                  active
                    ? "bg-foreground text-white"
                    : "text-muted hover:text-foreground hover:bg-zinc-50"
                }`}
              >
                <Icon className="w-3.5 h-3.5" weight={active ? "bold" : "regular"} />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-0 md:px-0 -mt-2">
        {tab === "documentacion" && <SolicitudesDocPage embedded />}
        {tab === "incidencias" && (
          <IncidenciasFeed
            tipo="incidencia"
            canSee={isGestor}
            currentUser={user}
            emptyReason={!isGestor ? "Solo gestores y admin pueden ver este panel." : null}
          />
        )}
        {tab === "ampliaciones" && (
          <IncidenciasFeed
            tipo="ampliacion"
            canSee={isGestor}
            currentUser={user}
            emptyReason={!isGestor ? "Solo gestores y admin pueden ver este panel." : null}
          />
        )}
        {tab === "recordatorios" && (
          <RecordatoriosFeed currentUser={user} />
        )}
      </div>

      {tab === "documentacion" && !isColector && (
        <div className="px-4 md:px-6 max-w-6xl mx-auto -mt-2">
          <p className="text-[11.5px] text-muted italic">
            Solo los usuarios marcados como "Recibe solicitudes" verán bandeja
            de documentación.
          </p>
        </div>
      )}
    </div>
  );
}
